// Organizations — the tenancy root (`06` §3.3, `SC-F100`–`SC-F109`).
//
// Naming discipline, enforced in code and in copy (`concept.md` §5.4): this is
// an **organization**. Never a tenant, never a team, never an account. "Tenant"
// is reserved for the *customer's own* end-customers and is always qualified.
//
// This module also owns the role ladder, because every screen in the product
// gates on it and there must be exactly one definition of "is this caller at
// least a maintainer".

import {
  del,
  getJSON,
  getWithETag,
  newIdempotencyKey,
  query,
  sendJSON,
  type Page,
} from "./http.js";

const BASE = "/v1/orgs";

/**
 * Role is a caller's membership role on one organization (`SC-F102`), TOTALLY
 * ORDERED: viewer < member < maintainer < owner. The total order is what lets a
 * single [atLeast] comparison express the invite role picker, the
 * member-management predicate and every role-gated control in the UI.
 */
export type Role = "viewer" | "member" | "maintainer" | "owner";

/** ROLES is the ladder in ascending order of authority. Index into it for a
 * role picker; never re-sort it. */
export const ROLES: readonly Role[] = ["viewer", "member", "maintainer", "owner"];

/** ROLE_LABELS is the UI copy for each rung, with the one-line description a
 * role picker shows underneath it. */
export const ROLE_LABELS: Readonly<Record<Role, { label: string; hint: string }>> = {
  viewer: { label: "Viewer", hint: "Read everything in the organization. Changes nothing." },
  member: {
    label: "Member",
    hint: "Read, plus run validation, tests and explain against an instance.",
  },
  maintainer: {
    label: "Maintainer",
    hint: "Author and publish policy, operate instances, manage members below owner.",
  },
  owner: {
    label: "Owner",
    hint: "Everything, plus create and delete instances, billing and ownership.",
  },
};

/**
 * atLeast reports whether `role` sits at or above `minimum` on the ladder. Use
 * it to gate a control rather than comparing strings:
 *
 *   ${atLeast(this.role, "maintainer") ? html`<bn-button>Publish</bn-button>` : ""}
 *
 * An unknown role — a newer server with a rung this bundle does not know —
 * resolves below every rung, which fails closed.
 */
export function atLeast(role: Role | undefined, minimum: Role): boolean {
  const have = ROLES.indexOf(role as Role);
  return have >= 0 && have >= ROLES.indexOf(minimum);
}

/**
 * Organization is one tenancy root. It mirrors the server's `orgInfo`
 * (`internal/accounts/orgs.go`) field for field.
 *
 * **`name` is the RFC-1123 label, not the prose.** That reads backwards until
 * you know where it comes from: `05` §3.3 and `migrations/0005` spell the
 * storage columns `name` (the label that appears in URLs and in generated
 * hostnames) and `display_name` (the human string), and the handler puts both
 * on the wire unchanged. `06` §3.1's `/v1/me` example spells the label `slug`
 * and is wrong; it has been corrected.
 *
 * The two other organization projections in this product — the operator console
 * (`backoffice.ts`) and the anonymous invite lookup (`invites.ts`) — DO answer
 * `{slug, name}`, because they are separate resources that rename for their
 * audience. Do not "fix" them to match this one.
 */
export interface Organization {
  id: string;
  /** The RFC-1123 label. Unique, appears in URLs and in instance hostnames. */
  name: string;
  /** The human display name. Empty falls back to [name]. */
  display_name: string;
  /** True for the automatically created personal organization (`SC-F101`). */
  personal: boolean;
  /** The caller's own role. Present on list and read routes. */
  role?: Role;
  /** `SC-F503`'s per-organization switch: omit Cloud-authored provenance from
   * rendered YAML. */
  suppress_provenance?: boolean;
  created: string;
  updated: string;
}

/** CreateOrgInput creates a shared organization. `POST /v1/orgs` is `allOrgs`:
 * a token narrowed to one organization must not create resources outside the
 * scope it was narrowed to.
 *
 * `name` is the RFC-1123 label and is required; `display_name` is the prose and
 * defaults to `name` server-side when omitted. */
export interface CreateOrgInput {
  name: string;
  display_name?: string;
}

/** listOrgs returns the organizations the caller belongs to (`SC-F109`). */
export function listOrgs(): Promise<Page<Organization>> {
  return getJSON<Page<Organization>>(BASE);
}

/** getOrg reads one organization. */
export function getOrg(orgId: string): Promise<Organization> {
  return getJSON<Organization>(`${BASE}/${encodeURIComponent(orgId)}`);
}

/**
 * getOrgWithETag reads one organization **and** its `ETag`, which is what
 * [updateOrg] then sends back as `If-Match` (`06` §1.7). Any screen that reads
 * an organization in order to save it must use this rather than [getOrg]: a
 * PATCH without the tag silently overwrites a colleague's concurrent rename
 * instead of answering `412 precondition_failed`.
 */
export function getOrgWithETag(orgId: string): Promise<{ value: Organization; etag: string }> {
  return getWithETag<Organization>(`${BASE}/${encodeURIComponent(orgId)}`);
}

/** createOrg creates a shared organization (`SC-F100`). Owner is the caller. */
export function createOrg(input: CreateOrgInput): Promise<Organization> {
  return sendJSON<Organization>(BASE, "POST", input, { idempotencyKey: newIdempotencyKey() });
}

/** updateOrg renames an organization or changes its settings (`SC-F106`).
 * Owner-only. Send the `ETag` from [getOrg] as `ifMatch`. */
export function updateOrg(
  orgId: string,
  patch: Partial<Pick<Organization, "name" | "display_name">>,
  opts: { ifMatch?: string } = {},
): Promise<Organization> {
  return sendJSON<Organization>(`${BASE}/${encodeURIComponent(orgId)}`, "PATCH", patch, opts);
}

/**
 * deleteOrg deletes an organization with cascade guards (`SC-F108`, v1).
 * Owner-only, step-up gated, and **absent from the scoped-token route map**: a
 * scoped token must not be able to delete the organization it is scoped to.
 * `SC-F108` refuses while any instance still exists.
 */
export function deleteOrg(orgId: string, opts: { verification: string }): Promise<void> {
  return del(`${BASE}/${encodeURIComponent(orgId)}`, {
    idempotencyKey: newIdempotencyKey(),
    verification: opts.verification,
  });
}

/**
 * transferOwnership nominates a new owner; the nominee must accept (`SC-F107`).
 * Step-up gated at MVP, because promoting an owner is the only membership change
 * that grants authority the actor cannot later revoke.
 */
export function transferOwnership(
  orgId: string,
  accountId: string,
  opts: { verification: string },
): Promise<void> {
  return sendJSON<void>(
    `${BASE}/${encodeURIComponent(orgId)}/transfer-ownership`,
    "POST",
    { account: accountId },
    { idempotencyKey: newIdempotencyKey(), verification: opts.verification },
  );
}

// ───────────────────────────────────────────── activity, plan, quotas ──

/** ActivityEvent is one row of the control-plane activity feed (`SC-F817`) —
 * who did what in this organization. Distinct from the per-query audit trail,
 * which is per instance and lives in `api/audit.ts`; `internal/audit` and
 * `internal/queryaudit` may not even import each other. */
export interface ActivityEvent {
  id: string;
  created: string;
  /** Dotted action name: `instance.created`, `config.published`, `member.role_changed`. */
  action: string;
  actor: { account: string | null; label: string; kind: "session" | "api_key" | "system" };
  /** The affected record, when the action names one. */
  target?: { kind: string; id: string; label: string };
  /** Never carries secret material — the body is served verbatim (`06` §1.4). */
  metadata?: Record<string, unknown>;
}

/** listActivity returns the organization's control-plane activity feed. */
export function listActivity(
  orgId: string,
  params: { limit?: number; cursor?: string; from?: string; to?: string; action?: string } = {},
): Promise<Page<ActivityEvent>> {
  return getJSON<Page<ActivityEvent>>(
    `${BASE}/${encodeURIComponent(orgId)}/activity${query({ ...params })}`,
  );
}

/** Quota is one limit and its current consumption (`SC-F901`). */
export interface Quota {
  quota: string;
  limit: number;
  current: number;
}

/** getQuotas returns every limit and its consumption. The same `{quota, limit,
 * current}` triple appears in a `quota_exceeded` error's `details`, so one
 * renderer covers both. */
export function getQuotas(orgId: string): Promise<{ items: Quota[] }> {
  return getJSON<{ items: Quota[] }>(`${BASE}/${encodeURIComponent(orgId)}/quotas`);
}

/**
 * Plan is the organization's entitlements (`SC-F900`, `SC-F901`). Read-only:
 * plan changes are not self-service in MVP or v1, and the UI must not pretend
 * otherwise (`concept.md` §7 principle 8 — honest labels).
 */
export interface Plan {
  id: string;
  name: string;
  audit_retention_days: number;
  quotas: Quota[];
  /** Feature flags the plan enables, by name. */
  features: string[];
}

/** getPlan returns the organization's plan and entitlements. */
export function getPlan(orgId: string): Promise<Plan> {
  return getJSON<Plan>(`${BASE}/${encodeURIComponent(orgId)}/plan`);
}

/**
 * UsageRollup is a metering ledger rollup (`SC-F819`, v1).
 *
 * TODO(SC-F819): the bucket shape is defined from `06` §3.3 and `04` §6; the Go
 * handler is v1 and does not exist yet.
 */
export interface UsageRollup {
  from: string;
  to: string;
  buckets: Array<{ day: string; metric: string; value: number }>;
}

/** getUsage returns the metering rollup for a half-open `[from, to)` window
 * (`06` §1.6: RFC 3339 with an explicit offset; a naive timestamp is 422). */
export function getUsage(
  orgId: string,
  params: { from: string; to: string },
): Promise<UsageRollup> {
  return getJSON<UsageRollup>(`${BASE}/${encodeURIComponent(orgId)}/usage${query({ ...params })}`);
}

/* ── SC-F112: per-organization security settings ──────────────────────────── */

/**
 * SecurityLimits is the honest half of the security payload: for every switch,
 * the boundary of what the running build actually enforces
 * (`concept.md` §7 principle 8).
 *
 * The server sends it with every read and the UI is required to render it next
 * to the control. A limit that lives only in a comment is a limit the screen
 * will eventually state wrongly — which is the exact failure this feature's
 * previous surface had, where four switches were badged "not enforced" and two
 * of them already were.
 */
export interface SecurityLimits {
  /** `enrolled_only`: an emailed code is refused for members who HAVE a passkey,
   * and permitted for members who do not. Invariant NL-1 (`01` §1.1) is why:
   * every account keeps a method that needs no prior enrollment, so a switch
   * that refused OTP for a member with no passkey would be a lockout with a
   * checkbox rather than a control. */
  passkey_enforcement: string;
  /** `linked_only`: same shape for `SC-F020`'s enforced SSO — refused for
   * accounts that have completed an SSO login, permitted for accounts that have
   * not. */
  sso_enforcement: string;
  /** `control_plane_api`: the allowlist covers this API and the console. It
   * cannot cover the customer's query path, which never traverses the control
   * plane (`concept.md` §1.2), and an owner who believed otherwise would have
   * been misled by us. */
  ip_allowlist_scope: string;
  /** `deployment_wide`: session lifetime is NOT per organization at this build.
   * The token's duration is a property of the auth collection and one session
   * spans every organization an account belongs to, so the control is absent
   * rather than offered — and this value is what lets the screen say so. */
  session_lifetime: string;
}

/** OrgSecurity is one organization's security posture (`SC-F112`). Every field
 * is enforced by the running build; `limits` states where each one stops. */
export interface OrgSecurity {
  require_passkey: boolean;
  /** `SC-F020`'s per-organization enforced SSO. Surfaced here because an owner
   * configuring authentication expects one screen. */
  enforced_sso: boolean;
  /** CIDR ranges from which this organization's resources may be reached.
   * Empty means no restriction. */
  ip_allowlist: string[];
  /** `SC-F709`'s opt-in half of the pre-publish gate. */
  require_test_gate: boolean;
  /** `SC-F503`: omit Cloud-authored labels from rendered YAML. */
  suppress_provenance: boolean;
  /** `SC-F806`'s window, READ-ONLY here: `01` §4.4 row 50 puts retention behind
   * the `billing` feature while this object carries `members`. */
  audit_retention_days: number;
  limits: SecurityLimits;
}

/** getSecurity reads the posture. Any member may read it — a member refused an
 * emailed sign-in code has to be able to discover why. */
export function getSecurity(orgId: string): Promise<OrgSecurity> {
  return getJSON<OrgSecurity>(`${BASE}/${encodeURIComponent(orgId)}/security`);
}

/**
 * updateSecurity writes the posture. Owner-only.
 *
 * Every field is optional and an ABSENT field is unchanged (`06` §1.10). Sending
 * a whole object read from [getSecurity] would work, but sending only what
 * changed is what makes two owners editing two switches in two tabs a
 * last-write-wins on their own switch rather than on each other's.
 *
 * The one refusal worth handling by hand is `409 conflict` with
 * `details.reason = "ip_self_exclusion"`: an allowlist that does not contain the
 * caller's own address is refused, because saving it would lock the organization
 * out of the only surface that can change it.
 */
export function updateSecurity(
  orgId: string,
  patch: Partial<
    Pick<
      OrgSecurity,
      | "require_passkey"
      | "enforced_sso"
      | "ip_allowlist"
      | "require_test_gate"
      | "suppress_provenance"
    >
  >,
  opts: { ifMatch?: string } = {},
): Promise<OrgSecurity> {
  return sendJSON<OrgSecurity>(
    `${BASE}/${encodeURIComponent(orgId)}/security`,
    "PATCH",
    patch,
    opts,
  );
}

/* ── SC-F914: separation-of-duties duty classes ───────────────────────────── */

/**
 * Duty is one of the four separation-of-duties classes `01` §4.4 tags every
 * permission-matrix row with. They are NOT a second role ladder and they are not
 * ordered: they partition ACTIONS, and a membership carries a role MINUS zero or
 * more of them.
 */
export type Duty = "author" | "operate" | "observe" | "administer";

/** DUTIES is the vocabulary in the server's own order, so a checklist renders
 * the same sequence the payload uses. */
export const DUTIES: readonly Duty[] = ["author", "operate", "observe", "administer"];

/** DUTY_LABELS is the UI copy for each class, transcribed from `01` §4.3's table
 * so the screen and the specification say the same thing. */
export const DUTY_LABELS: Readonly<Record<Duty, { label: string; hint: string }>> = {
  author: {
    label: "Author",
    hint: "Policy and datasource authoring, drafts, publish, rollback, server-config edits.",
  },
  operate: {
    label: "Operate",
    hint: "Instance lifecycle, power operations, sizing, upgrades, credential rotation.",
  },
  observe: {
    label: "Observe",
    hint: "Audit read, analytics, the activity feed, logs and status.",
  },
  administer: {
    label: "Administer",
    hint: "Membership, roles, invitations, organization settings and billing.",
  },
};

/** MemberDuties is one membership's duty configuration (`SC-F914`). */
export interface MemberDuties {
  account_id: string;
  role: Role;
  /** The subtracted classes, sorted. */
  excluded_duties: Duty[];
  /** The classes this membership actually holds: the rung is high enough AND the
   * class is not subtracted. Computed server-side so the browser does not have
   * to ship the rung-per-class table. */
  held_duties: Duty[];
}

/** getDuties reads one membership's duty configuration. Readable at viewer,
 * because the console has to be able to explain a MISSING control. */
export function getDuties(orgId: string, accountId: string): Promise<MemberDuties> {
  return getJSON<MemberDuties>(
    `${BASE}/${encodeURIComponent(orgId)}/members/${encodeURIComponent(accountId)}/duties`,
  );
}

/**
 * setDuties replaces one membership's excluded classes. Owner-only, and a PUT
 * rather than a PATCH because the set is absolute: a differential update to a
 * security control makes "remove operate" and "the operate box is unchecked"
 * two requests with one intent and different outcomes under concurrency.
 *
 * The refusal to handle by hand is `409 conflict` with
 * `details.reason = "duty_coverage"` and `details.duties` naming the classes:
 * the server refuses a configuration that would leave the organization with
 * nobody holding a class, and the UI must show that reason rather than saving.
 */
export function setDuties(
  orgId: string,
  accountId: string,
  excluded: Duty[],
): Promise<MemberDuties> {
  return sendJSON<MemberDuties>(
    `${BASE}/${encodeURIComponent(orgId)}/members/${encodeURIComponent(accountId)}/duties`,
    "PUT",
    { excluded_duties: excluded },
  );
}
